// src/components/SidebarMenu/SidebarMenu.js
import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { XIcon, ChevronLeftIcon } from '@heroicons/react/outline'
import { UserContext } from '../../hooks/UserContext'
import { useQuery } from 'react-query'
import { fetchWarehousesOpen } from '../../services/warehouseSevices'

import { navigation, isSidebarItemVisibleForWarehouse } from './sidebarNavigation'
import { userHasPermission } from '../../navigation/NavegationRoutes'
import { useWarehouseResolver } from './useWarehouseResolver'
import { useNaturaVisibility } from './useNaturaVisibility'
import SidebarMenuItem from './SidebarMenuItem'
import SidebarConfigSection from './SidebarConfigSection'
import SidebarNaturaOpsSection from './SidebarNaturaOpsSection'
import WarehouseSelector from './WarehouseSelector'
import UserProfileSection from './UserProfileSection'
import MobileTopBar from './MobileTopBar'
import { ClaimsPendingCountBadge } from '../../pages/Claims/ClaimsPendingCountBadge'
import { FulfillmentErrorsPendingRetryBadge } from '../../pages/Orders/FulfillmentErrorsPendingRetryBadge'
import { CLAIMS, FULFILLMENT_ERRORS } from '../../navigation/constants'
import cubboLogo from '../../assets/cubbo-white.png'

const STORAGE_KEY = 'cubbo_wms_sidebar_collapsed'

// One badge per menu entry. With two it no longer fits in the item line's ternary without
// making it unreadable, and the next one added goes here without touching the JSX.
const badgeFor = (href, warehouseId) => {
    if (href === CLAIMS) return <ClaimsPendingCountBadge warehouseId={warehouseId} />
    if (href === FULFILLMENT_ERRORS) return <FulfillmentErrorsPendingRetryBadge warehouseId={warehouseId} />
    return null
}

export default function SidebarMenu() {
    const context = useContext(UserContext)
    const user = context?.user
    const history = useHistory()

    const [isCollapsed, setIsCollapsed] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === 'true' } catch { return false }
    })

    const [isMobileOpen, setIsMobileOpen] = useState(false)

    useEffect(() => {
        const unlisten = history.listen(() => setIsMobileOpen(false))
        return unlisten
    }, [history])

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => {
            const next = !prev
            try { localStorage.setItem(STORAGE_KEY, String(next)) } catch {}
            return next
        })
    }, [])

    const { data: openWarehouses } = useQuery(
        'warehouses_open',
        fetchWarehousesOpen,
        { refetchOnWindowFocus: false }
    )

    const warehouse_id = useWarehouseResolver(user, context?.setUser, openWarehouses)
    const isNaturaVisible = useNaturaVisibility(warehouse_id, user)

    const currentWarehouse = user?.current_warehouse

    const hasWarehouse = Boolean(warehouse_id)

    const visibleItems = user && hasWarehouse
        ? navigation.filter(
              (item) =>
                  userHasPermission(user, item.permission_key) &&
                  isNaturaVisible(item) &&
                  (typeof item.isVisible !== 'function' || item.isVisible(user)) &&
                  isSidebarItemVisibleForWarehouse(item, currentWarehouse)
          )
        : []

    const renderSidebarContent = (collapsed) => (
        <div className="flex flex-col h-full">
            <div className="px-4 py-5 flex items-center gap-2.5 border-b" style={{ borderColor: '#ebebeb' }}>
                {collapsed ? (
                    <button onClick={toggleCollapse} className="mx-auto" aria-label="Expand sidebar">
                        <div className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer" style={{ backgroundColor: '#DCA118' }}>
                            <img src={cubboLogo} alt="Cubbo" className="w-5 h-5 object-contain" />
                        </div>
                    </button>
                ) : (
                    <>
                        <button onClick={toggleCollapse} className="flex items-center gap-2.5 flex-1 min-w-0" aria-label="Collapse sidebar">
                            <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 cursor-pointer" style={{ backgroundColor: '#DCA118' }}>
                                <img src={cubboLogo} alt="Cubbo" className="w-5 h-5 object-contain" />
                            </div>
                            <span className="font-bold text-base text-gray-900">Cubbo WMS</span>
                        </button>
                        <ChevronLeftIcon className="w-4 h-4 flex-shrink-0 cursor-pointer" style={{ color: '#DCA118' }} onClick={toggleCollapse} />
                    </>
                )}
            </div>


            <WarehouseSelector warehouse={currentWarehouse} isCollapsed={collapsed} />

            <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5" aria-label="Main navigation">
                {visibleItems.map((item) => (
                    <SidebarMenuItem
                        key={item.id}
                        item={item}
                        warehouseId={warehouse_id}
                        isCollapsed={collapsed}
                        badge={badgeFor(item.href, warehouse_id)}
                    />
                ))}
                {hasWarehouse && <SidebarNaturaOpsSection warehouseId={warehouse_id} isCollapsed={collapsed} onExpand={toggleCollapse} />}
                {hasWarehouse && <SidebarConfigSection warehouseId={warehouse_id} isCollapsed={collapsed} onExpand={toggleCollapse} />}
            </nav>

            <UserProfileSection isCollapsed={collapsed} onExpand={toggleCollapse} />
        </div>
    )

    return (
        <>
            <MobileTopBar
                warehouseName={currentWarehouse?.name || ''}
                onOpenSidebar={() => setIsMobileOpen(true)}
            />

            <aside
                className="hidden lg:flex flex-col flex-shrink-0 bg-white border-r transition-all duration-200 overflow-hidden"
                style={{
                    width: isCollapsed ? '64px' : '256px',
                    borderColor: '#ebebeb',
                }}
            >
                {renderSidebarContent(isCollapsed)}
            </aside>

            {isMobileOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50"
                        onClick={() => setIsMobileOpen(false)}
                        aria-hidden="true"
                    />
                    <div
                        className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl flex flex-col z-50"
                        style={{ animation: 'slideInLeft 250ms ease-out' }}
                    >
                        <div className="absolute top-4 right-4">
                            <button onClick={() => setIsMobileOpen(false)} aria-label="Close menu">
                                <XIcon className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>
                        {renderSidebarContent(false)}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideInLeft {
                    from { transform: translateX(-100%); }
                    to { transform: translateX(0); }
                }
            `}</style>
        </>
    )
}
